import { ReactNode, useEffect, useState } from "react";
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Top-level grouping container for the ExportDetail page. Wraps the
// existing per-feature Paper cards in a header + collapse so the user
// can fold sections whose work has moved past them (Job Activity once
// the workflow terminates, Result Data once a search env is up).
//
// ``defaultOpen`` only fires the initial value; ``forceOpen`` (when
// provided) re-syncs whenever its value changes — used by ExportDetail
// to auto-collapse Job Activity the moment the workflow reaches a
// terminal status, even if the user previously expanded it.
interface Props {
  title: string;
  subtitle?: string;
  // Initial expanded state on first mount. Defaults to true.
  defaultOpen?: boolean;
  // When provided, syncs the open state to this value on every
  // change. ``undefined`` means user-controlled only.
  forceOpen?: boolean;
  children: ReactNode;
}

export function CollapsibleSection({
  title,
  subtitle,
  defaultOpen = true,
  forceOpen,
  children,
}: Props) {
  const [open, setOpen] = useState(forceOpen ?? defaultOpen);
  useEffect(() => {
    if (forceOpen !== undefined) setOpen(forceOpen);
  }, [forceOpen]);

  return (
    <Paper variant="outlined" sx={{ mb: 2, overflow: "hidden" }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => setOpen((v) => !v)}
        sx={{
          px: 2,
          py: 1.25,
          cursor: "pointer",
          userSelect: "none",
          backgroundColor: "background.default",
          borderBottom: open ? "1px solid" : "none",
          borderColor: "divider",
          "&:hover": { backgroundColor: "action.hover" },
        }}
      >
        <IconButton size="small" sx={{ p: 0.25 }} tabIndex={-1}>
          {open ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Stack>
      <Collapse in={open} unmountOnExit={false}>
        <Box sx={{ p: 2 }}>{children}</Box>
      </Collapse>
    </Paper>
  );
}
